import { inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

const pathSegments = (path: string): string[] => path.split(/[?#]/)[0].split('/').filter(Boolean);

/** The first path segment of a URL or of a router link — what a nav item's `route` is written as. */
export const navSegment = (path: string): string => pathSegments(path)[0] ?? '';

/** A URL or router link as an absolute path, without its query, fragment or trailing slash. */
export const navPath = (path: string): string => ['', ...pathSegments(path)].join('/') || '/';

const injectFromUrl = <T>(project: (url: string) => T): Signal<T> => {
  const router = inject(Router);

  return toSignal(
    router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => project(router.url)),
    ),
    { initialValue: project(router.url) },
  );
};

/**
 * The segment of the page on screen, re-read after every navigation.
 *
 * The nav surfaces ask this rather than leaning on `routerLinkActive`, which
 * can only match a link against its own URL: an item that stands for more than
 * one segment — Profile, which is also where Settings is reached from — needs
 * the segment itself. See `isNavItemActive`.
 */
export const injectNavSegment = (): Signal<string> => injectFromUrl(navSegment);

/** The path of the page on screen, re-read after every navigation. See `isSidenavEntryActive`. */
export const injectNavPath = (): Signal<string> => injectFromUrl(navPath);
